/**
 * Certificado mTLS do dispositivo, dentro do detalhe do dispositivo — Nexora Grid (frontend, US-2002, P-010).
 *
 * Criado em: 19/10/2026 · Última modificação: 19/10/2026 · Versão: 1.0.0
 *
 * A Nexora e o administrador e a engenharia da própria empresa emitem, baixam (uma vez), renovam e revogam. A senha
 * do pacote aparece uma única vez, logo após a emissão — a tela pede para anotar antes de sair. Quem vê a operadora
 * consulta a lista (sem chave nem senha).
 */
import { useCallback, useEffect, useState } from "react";
import { api, baixar, ErroApi } from "../api";
import { quando } from "./tipos";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";

type Certificado = { serial: string; emitido_em: string; valido_ate: string; emitido_por: string; renovacao: boolean;
  situacao: "em_vigor" | "substituido" | "revogado" | "vencido"; pacote_disponivel: boolean; baixado_em: string | null;
  baixado_por: string | null; revogado_em: string | null; motivo_revogacao: string | null; dias_para_vencer: number; vence_em_breve: boolean };
type Lista = { certificados: Certificado[]; pode_gerenciar: boolean; emissao_disponivel: boolean };
type Emissao = { serial: string; valido_ate: string; renovacao: boolean; senha: string; aviso: string };

const SITUACAO: Record<Certificado["situacao"], [string, string]> = { em_vigor: ["Em vigor", "situacao-validado"],
  substituido: ["Substituído", "situacao-pendente"], revogado: ["Revogado", "situacao-rejeitado"], vencido: ["Vencido", "situacao-rejeitado"] };

export function Certificados({ deviceId, ativo }: { deviceId: string; ativo: boolean }) {
  const { usuario } = useSessao();
  const [dados, setDados] = useState<Lista | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [emissao, setEmissao] = useState<Emissao | null>(null);
  const [validade, setValidade] = useState(365);
  const [motivo, setMotivo] = useState<Record<string, string>>({});
  const base = `/v1/admin/dispositivos/${deviceId}/certificados`;
  const carregar = useCallback(() => { api<Lista>(base).then(setDados).catch(() => setDados(null)); }, [base]);
  const capacidade = !!usuario && pode.gerenciarCertificados(usuario);
  useEffect(carregar, [carregar, capacidade]);
  const falhar = (x: unknown) => setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede.");
  const emitir = async () => {
    try { setEmissao(await api<Emissao>(base, { corpo: { validade_dias: validade } })); setAviso(null); carregar(); }
    catch (x) { falhar(x); }
  };
  const pacote = async (serial: string) => {
    try { await baixar(`${base}/${serial}/pacote`, `${deviceId}-certificado.zip`); setAviso("Pacote baixado. Ele não fica mais disponível na plataforma."); carregar(); }
    catch (x) { falhar(x instanceof ErroApi && x.status === 410 ? new ErroApi(410, "pacote já baixado ou vencido — emita outro certificado") : x); }
  };
  const revogar = async (serial: string) => {
    try { await api(`${base}/${serial}/revogar`, { corpo: { motivo: motivo[serial] ?? "" } }); setAviso("Certificado revogado: o broker passa a recusá-lo em até 1 minuto."); carregar(); }
    catch (x) { falhar(x); }
  };
  if (!dados) return null;
  const gerencia = dados.pode_gerenciar && capacidade;
  const vigente = dados.certificados.find((c) => c.situacao === "em_vigor");
  return (
    <section className="filiais" aria-label="Certificado do dispositivo">
      <h3>Certificado mTLS</h3>
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      {gerencia && emissao && (
        <div className="nx-cartao aviso-admin senha-certificado" role="alert">
          <p><b>{emissao.renovacao ? "Certificado renovado." : "Certificado emitido."}</b> {emissao.aviso}</p>
          <p>Senha do pacote: <code className="nx-valor" aria-label="Senha do pacote">{emissao.senha}</code></p>
          <button type="button" className="nx-botao nx-botao-principal" onClick={() => void pacote(emissao.serial)}>Baixar pacote</button>{" "}
          <button type="button" className="nx-botao" onClick={() => setEmissao(null)}>Já anotei a senha</button>
        </div>
      )}
      {vigente?.vence_em_breve && <p className="erro">O certificado em vigor vence em {vigente.dias_para_vencer} dia(s): renove e instale no equipamento.</p>}
      <div className="tabela-rolagem">
        <table className="tabela tabela-usuarios">
          <thead><tr><th>Serial</th><th>Situação</th><th>Emitido</th><th>Válido até</th><th>Pacote</th>{gerencia && <th />}</tr></thead>
          <tbody>
            {dados.certificados.map((c) => (
              <tr key={c.serial}>
                <td className="nx-valor" title={c.serial}>{c.serial.slice(0, 12)}…</td>
                <td><span className={`nx-selo ${SITUACAO[c.situacao][1]}`}>{SITUACAO[c.situacao][0]}</span>
                  {c.revogado_em && <small className="nx-suave"> · {c.motivo_revogacao}</small>}</td>
                <td>{quando(c.emitido_em)} <small className="nx-suave">por {c.emitido_por}</small></td>
                <td>{quando(c.valido_ate)}</td>
                <td>{c.baixado_em ? <small className="nx-suave">baixado por {c.baixado_por}</small> : c.pacote_disponivel ? "aguardando download" : "—"}</td>
                {gerencia && (
                  <td className="acoes-linha">
                    {c.pacote_disponivel && !emissao && <button type="button" className="nx-botao" onClick={() => void pacote(c.serial)}>Baixar pacote</button>}
                    {(c.situacao === "em_vigor" || c.situacao === "substituido") && <>
                      <input aria-label={`Motivo da revogação do certificado ${c.serial.slice(0, 12)}`} placeholder="motivo" maxLength={200}
                        value={motivo[c.serial] ?? ""} onChange={(e) => setMotivo({ ...motivo, [c.serial]: e.target.value })} />
                      <button type="button" className="nx-botao" disabled={(motivo[c.serial] ?? "").trim().length < 5} onClick={() => void revogar(c.serial)}>Revogar</button>
                    </>}
                  </td>
                )}
              </tr>
            ))}
            {!dados.certificados.length && <tr><td colSpan={6} className="nx-suave">nenhum certificado emitido pela plataforma</td></tr>}
          </tbody>
        </table>
      </div>
      {gerencia && ativo && (dados.emissao_disponivel ? (
        <div className="acoes-conta">
          <div className="nx-campo"><label htmlFor="cert-validade">Validade</label>
            <select id="cert-validade" value={validade} onChange={(e) => setValidade(Number(e.target.value))}>
              <option value={365}>1 ano</option><option value={730}>2 anos</option><option value={90}>90 dias</option>
            </select></div>
          <button type="button" className="nx-botao" onClick={() => void emitir()}>{vigente ? "Renovar certificado" : "Emitir certificado"}</button>
        </div>
      ) : <p className="nx-suave">Emissão pela plataforma indisponível neste ambiente (ca-service sem o token do backend).</p>)}
    </section>
  );
}
